"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";

type Language = "EN" | "RO" | "BG";

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
  getProcessSteps: () => any[];
  getQualityIndicators: () => any[];
  getSocialLinks: () => any[];
  getFooterSocialLinks: () => any[];
}

const translations = {
  EN: {
    // Navigation
    "navigation.gallery": "Gallery",
    "navigation.process": "Process",
    "navigation.about": "About",
    "navigation.contact": "Contact",
    "navigation.switchToBulgarian": "Switch to Bulgarian",
    "navigation.switchToBulgarian": "Switch to Bulgarian",
    "navigation.switchToEnglish": "Switch to English",
    
    // Hero
    "hero.tagline": "Capturing moments, creating memories.",
    "hero.description": "Professional photography services with artistic vision and technical excellence.",
    "hero.explore": "Explore my work",
    
    // Gallery
    "gallery.title": "Photo Art Gallery",
    "gallery.description": "A collection of moments captured through the lens, showcasing the beauty of God's creation in various forms and perspectives.",
    "gallery.filterLabel": "Filter by category",
    "gallery.showing": "Showing {count} of {total} images",
    "gallery.inCategory": " in {category}",
    "gallery.clickToView": "Click any image to view details",
    "gallery.loadMore": "Load More Images",
    "gallery.showLess": "Show Less",
    "gallery.allWork": "All Work",
    "gallery.noImages": "No images found",
    "gallery.emptyGallery": "The gallery is currently empty. Please check back later.",
    "gallery.noImagesInCategory": "No images found in the \"{category}\" category. Try selecting \"All Work\" or another category.",
    "gallery.viewAllWork": "View All Work",

    // Commercial Gallery
    "commercialGallery.title": "Commercial Photography",
    "commercialGallery.description": "Commercial photography for businesses — clean, professional images that showcase your space. Perfect for Google Maps, websites, and social media.",
    "commercialGallery.filterLabel": "Filter by category",
    "commercialGallery.showing": "Showing {count} of {total} images",
    "commercialGallery.inCategory": " in {category}",
    "commercialGallery.clickToView": "Click any image to view details",
    "commercialGallery.loadMore": "Load More Images",
    "commercialGallery.showLess": "Show Less",
    "commercialGallery.allWork": "All Work",
    "commercialGallery.noImages": "No images found",
    "commercialGallery.emptyGallery": "The gallery is currently empty. Please check back later.",
    "commercialGallery.noImagesInCategory": "No images found in the \"{category}\" category. Try selecting \"All Work\" or another category.",
    "commercialGallery.viewAllWork": "View All Work",
    
    // Process
    "process.title": "The Process",
    "process.description": "A meticulous approach to capturing the essence of every project, from concept to final delivery.",
    
    // About (formerly Awards/Recognition)
    "awards.title": "About & Excellence",
    "awards.description": "Committed to delivering exceptional quality and maintaining the highest standards in every project",
    "awards.professionalRecognition": "Professional Recognition",
    "awards.successfulProjects": "Successful Projects",
    "awards.clientSatisfaction": "Client Satisfaction",
    "awards.yearsExperience": "Years Experience",
    "awards.qualityCommitment": "Quality Commitment",
    "awards.professionalStandards": "Professional Standards",
    "awards.excellenceIndicator": "Excellence Indicator",
    "awards.qualityIndicators.0.title": "Award-Winning Quality",
    "awards.qualityIndicators.0.description": "Consistent recognition for excellence in photography",
    "awards.qualityIndicators.1.title": "Professional Standards",
    "awards.qualityIndicators.1.description": "Adherence to industry best practices and ethics",
    "awards.qualityIndicators.2.title": "Client Satisfaction",
    "awards.qualityIndicators.2.description": "Trusted by clients across multiple industries",
    "awards.qualityIndicators.3.title": "Years of Excellence",
    "awards.qualityIndicators.3.description": "8+ years of dedicated professional experience",
    "awards.closingStatement": "Every project is approached with the same dedication to excellence, attention to detail, and commitment to delivering results that exceed expectations.",
    
    // Social Card
    "social.title": "Connect & Explore",
    "social.description": "Review my complete portfolio on Flickr or message me directly on Facebook",
    "social.contactAvailable": "Contact Available",
    "social.portfolioReady": "Portfolio Ready",
    "social.facebook.name": "Facebook",
    "social.facebook.description": "Message me for inquiries & follow for updates",
    "social.facebook.stats": "Contact & Follow",
    "social.flickr.name": "Flickr",
    "social.flickr.description": "Review my full photography portfolio",
    "social.flickr.stats": "Full Portfolio",
    "social.clickToVisit": "Click to visit →",
    "social.availableForContact": "Available for contact",
    "social.connectOnSocial": "Connect on social media",
    "social.additionalInfo": "Contact me on Facebook for inquiries • Review full portfolio on Flickr • Regular updates",
    
    // Contact
    "contact.title": "Get in Touch",
    "contact.description": "Have a project in mind? Let's discuss how we can bring your vision to life.",
    "contact.name": "Name",
    "contact.namePlaceholder": "Your name",
    "contact.email": "Email",
    "contact.emailPlaceholder": "your.email@example.com",
    "contact.brief": "Project Brief",
    "contact.briefPlaceholder": "Tell me about your project, timeline, and any specific requirements...",
    "contact.minCharacters": "Minimum 20 characters",
    "contact.submit": "Send Message",
    "contact.submitting": "Sending...",
    "contact.successTitle": "Message Sent!",
    "contact.successMessage": "Thank you for your message. I'll get back to you within 24 hours.",
    "contact.privacyNotice": "Your information is secure and will only be used to respond to your inquiry.",
    "contact.directContact": "Prefer direct contact?",
    "contact.emailLabel": "Email me directly:",
    "contact.responseTime": "Response Time",
    "contact.hoursResponse": "I typically respond within 24 hours on weekdays.",
    "contact.secureSubmission": "Secure Submission",
    "contact.secureForm": "Secure form",
    "contact.quickResponse": "Quick response",
    
    // Footer
    "footer.photographer": "Professional Photographer",
    "footer.location": "Sofia, Bulgaria",
    "footer.rights": "Dumitrescu Photography. All rights reserved.",
    "footer.allRightsReserved": "All rights reserved.",
    "footer.connectWithMe": "Connect With Me",
    "footer.socialDescription": "Message me on Facebook for inquiries or review my complete portfolio on Flickr",
    "footer.facebook.name": "Facebook",
    "footer.facebook.visit": "Visit",
    "footer.flickr.name": "Flickr",
    "footer.flickr.visit": "Visit",
    "footer.emailAddress": "contact@dumitrescuphotography.com",
  },
  RO: {
    // Navigation
    "navigation.gallery": "Galerie",
    "navigation.process": "Proces",
    "navigation.about": "Despre",
    "navigation.contact": "Contact",
    "navigation.switchToBulgarian": "Comută la română",
    "navigation.switchToBulgarian": "Comută la bulgară",
    "navigation.switchToEnglish": "Comută la engleză",
    
    // Hero
    "hero.tagline": "Capturăm momente, creăm amintiri.",
    "hero.description": "Servicii profesionale de fotografie cu viziune artistică și excelență tehnică.",
    "hero.explore": "Explorează munca mea",
    
    // Gallery
    "gallery.title": "Galerie de Artă Fotografică",
    "gallery.description": "O colecție de momente capturate prin obiectiv, care evidențiază frumusețea creației lui Dumnezeu în diferite forme și perspective.",
    "gallery.filterLabel": "Filtrează după categorie",
    "gallery.showing": "Se afișează {count} din {total} imagini",
    "gallery.inCategory": " în categoria {category}",
    "gallery.clickToView": "Apasă pe orice imagine pentru a vedea detalii",
    "gallery.loadMore": "Încarcă mai multe imagini",
    "gallery.showLess": "Arată mai puțin",
    "gallery.allWork": "Toate lucrările",
    "gallery.noImages": "Nu s-au găsit imagini",
    "gallery.emptyGallery": "Galeria este în prezent goală. Te rugăm să revii mai târziu.",
    "gallery.noImagesInCategory": "Nu s-au găsit imagini în categoria \"{category}\". Încearcă să selectezi \"Toate lucrările\" sau o altă categorie.",
    "gallery.viewAllWork": "Vezi toate lucrările",
      
        // Gallery
    "commercialGallery.title": "Fotografie comercială",
    "commercialGallery.description": "Fotografie comercială pentru afaceri — imagini clare și profesionale ale spațiului vostru. Perfecte pentru Google Maps, site-uri web și rețele sociale.",
    "commercialGallery.filterLabel": "Filtrează după categorie",
    "commercialGallery.showing": "Se afișează {count} din {total} imagini",
    "commercialGallery.inCategory": " în categoria {category}",
    "commercialGallery.clickToView": "Apasă pe orice imagine pentru a vedea detalii",
    "commercialGallery.loadMore": "Încarcă mai multe imagini",
    "commercialGallery.showLess": "Arată mai puțin",
    "commercialGallery.allWork": "Toate lucrările",
    "commercialGallery.noImages": "Nu s-au găsit imagini",
    "commercialGallery.emptyGallery": "Galeria este în prezent goală. Te rugăm să revii mai târziu.",
    "commercialGallery.noImagesInCategory": "Nu s-au găsit imagini în categoria \"{category}\". Încearcă să selectezi \"Toate lucrările\" sau o altă categorie.",
    "commercialGallery.viewAllWork": "Vezi toate lucrările",
    
    // Process
    "process.title": "Procesul",
    "process.description": "O abordare meticuloasă pentru a captura esența fiecărui proiect, de la concept la livrarea finală.",
    
    // About (formerly Awards/Recognition)
    "awards.title": "Despre & Excelență",
    "awards.description": "Dedicație față de calitatea excepțională și menținerea celor mai înalte standarde în fiecare proiect",
    "awards.professionalRecognition": "Recunoaștere Profesională",
    "awards.successfulProjects": "Proiecte Reușite",
    "awards.clientSatisfaction": "Satisfacția Clienților",
    "awards.yearsExperience": "Ani de Experiență",
    "awards.qualityCommitment": "Angajamentul Calității",
    "awards.professionalStandards": "Standarde Profesionale",
    "awards.excellenceIndicator": "Indicator de Excelență",
    "awards.qualityIndicators.0.title": "Calitate Premiată",
    "awards.qualityIndicators.0.description": "Recunoaștere consistentă pentru excelență în fotografie",
    "awards.qualityIndicators.1.title": "Standarde Profesionale",
    "awards.qualityIndicators.1.description": "Respectarea celor mai bune practici și etică din industrie",
    "awards.qualityIndicators.2.title": "Satisfacția Clienților",
    "awards.qualityIndicators.2.description": "De încredere de către clienți din multiple industrii",
    "awards.qualityIndicators.3.title": "Ani de Excelență",
    "awards.qualityIndicators.3.description": "Peste 8 ani de experiență profesională dedicată",
    "awards.closingStatement": "Fiecare proiect este abordat cu aceeași dedicație față de excelență, atenție la detalii și angajament de a livra rezultate care depășesc așteptările.",
    
    // Social Card
    "social.title": "Conectează-te & Explorează",
    "social.description": "Vezi portofoliul meu complet pe Flickr sau trimite-mi un mesaj direct pe Facebook",
    "social.contactAvailable": "Contact Disponibil",
    "social.portfolioReady": "Portofoliu Pregătit",
    "social.facebook.name": "Facebook",
    "social.facebook.description": "Trimite-mi un mesaj pentru întrebări și urmărește-mă pentru actualizări",
    "social.facebook.stats": "Contact & Urmărire",
    "social.flickr.name": "Flickr",
    "social.flickr.description": "Vezi portofoliul meu complet de fotografie",
    "social.flickr.stats": "Portofoliu Complet",
    "social.clickToVisit": "Click pentru a vizita →",
    "social.availableForContact": "Disponibil pentru contact",
    "social.connectOnSocial": "Conectează-te pe rețelele sociale",
    "social.additionalInfo": "Contactează-mă pe Facebook pentru întrebări • Vezi portofoliul complet pe Flickr • Actualizări regulate",
    
    // Contact
    "contact.title": "Ia Legătura",
    "contact.description": "Ai un proiect în minte? Hai să discutăm cum putem da viață viziunii tale.",
    "contact.name": "Nume",
    "contact.namePlaceholder": "Numele tău",
    "contact.email": "Email",
    "contact.emailPlaceholder": "emailul.tau@exemplu.com",
    "contact.brief": "Descrierea Proiectului",
    "contact.briefPlaceholder": "Spune-mi despre proiectul tău, termenele limită și orice cerințe specifice...",
    "contact.minCharacters": "Minimum 20 de caractere",
    "contact.submit": "Trimite Mesaj",
    "contact.submitting": "Se trimite...",
    "contact.successTitle": "Mesaj Trimis!",
    "contact.successMessage": "Îți mulțumim pentru mesaj. Voi reveni la tine în maxim 24 de ore.",
    "contact.privacyNotice": "Informațiile tale sunt securizate și vor fi folosite doar pentru a răspunde la cererea ta.",
    "contact.directContact": "Preferi contact direct?",
    "contact.emailLabel": "Trimite-mi un email direct:",
    "contact.responseTime": "Timp de Răspuns",
    "contact.hoursResponse": "De obicei răspund în 24 de ore în timpul săptămânii.",
    "contact.secureSubmission": "Trimitere Securizată",
    "contact.secureForm": "Formular securizat",
    "contact.quickResponse": "Răspuns rapid",
    
    // Footer
    "footer.photographer": "Fotograf Profesionist",
    "footer.location": "București, România",
    "footer.rights": "Dumitrescu Photography. Toate drepturile rezervate.",
    "footer.allRightsReserved": "Toate drepturile rezervate.",
    "footer.connectWithMe": "Conectează-te cu mine",
    "footer.socialDescription": "Trimite-mi un mesaj pe Facebook pentru întrebări sau vezi portofoliul meu complet pe Flickr",
    "footer.facebook.name": "Facebook",
    "footer.facebook.visit": "Vizitează",
    "footer.flickr.name": "Flickr",
    "footer.flickr.visit": "Vizitează",
    "footer.emailAddress": "contact@dumitrescuphotography.com",
  },
  BG: {
    // Navigation
    "navigation.gallery": "Галерия",
    "navigation.process": "Процес",
    "navigation.about": "За мен",
    "navigation.contact": "Контакт",
    "navigation.switchToBulgarian": "Превключи на румънски",
    "navigation.switchToBulgarian": "Превключи на български",
    "navigation.switchToEnglish": "Превключи на английски",
    
    // Hero
    "hero.tagline": "Запазваме моменти, създаваме спомени.",
    "hero.description": "Професионални фотографски услуги с артистична визия и техническо майсторство.",
    "hero.explore": "Разгледайте моята работа",
    
    // Gallery
    "gallery.title": "Фотографска Галерия за Изкуство",
    "gallery.description": "Колекция от моменти, заснети през обектива, показващи красотата на Божието творение в различни форми и перспективи.",
    "gallery.filterLabel": "Филтрирай по категория",
    "gallery.showing": "Показва се {count} от {total} изображения",
    "gallery.inCategory": " в категория {category}",
    "gallery.clickToView": "Кликнете върху което и да е изображение, за да видите детайли",
    "gallery.loadMore": "Зареди още изображения",
    "gallery.showLess": "Покажи по-малко",
    "gallery.allWork": "Всички работи",
    "gallery.noImages": "Не са намерени изображения",
    "gallery.emptyGallery": "Галерията в момента е празна. Моля, проверете отново по-късно.",
    "gallery.noImagesInCategory": "Не са намерени изображения в категория \"{category}\". Опитайте да изберете \"Всички работи\" или друга категория.",
    "gallery.viewAllWork": "Вижте всички работи",
    
    //Commercial Gallery
    "commercialGallery.title": "Търговска фотография",
    "commercialGallery.description": "Професионална фотография за бизнес — чисти и качествени кадри на вашето пространство. Идеални за Google Maps, уебсайтове и социални мрежи.",
    "commercialGallery.filterLabel": "Филтрирай по категория",
    "commercialGallery.showing": "Показва се {count} от {total} изображения",
    "commercialGallery.inCategory": " в категория {category}",
    "commercialGallery.clickToView": "Кликнете върху което и да е изображение, за да видите детайли",
    "commercialGallery.loadMore": "Зареди още изображения",
    "commercialGallery.showLess": "Покажи по-малко",
    "commercialGallery.allWork": "Всички работи",
    "commercialGallery.noImages": "Не са намерени изображения",
    "commercialGallery.emptyGallery": "Галерията в момента е празна. Моля, проверете отново по-късно.",
    "commercialGallery.noImagesInCategory": "Не са намерени изображения в категория \"{category}\". Опитайте да изберете \"Всички работи\" или друга категория.",
    "commercialGallery.viewAllWork": "Вижте всички работи",
    // Process
    "process.title": "Процесът",
    "process.description": "Изчерпателен подход за заснемане на същността на всеки проект, от концепцията до крайното предаване.",
    
    // About (formerly Awards/Recognition)
    "awards.title": "За мен & Изключителност",
    "awards.description": "Посветени на изключително качество и поддържане на най-високите стандарти във всеки проект",
    "awards.professionalRecognition": "Професионално признание",
    "awards.successfulProjects": "Успешни проекти",
    "awards.clientSatisfaction": "Удовлетворение на клиентите",
    "awards.yearsExperience": "Години опит",
    "awards.qualityCommitment": "Ангажимент към качеството",
    "awards.professionalStandards": "Професионални стандарти",
    "awards.excellenceIndicator": "Индикатор за изключителност",
    "awards.qualityIndicators.0.title": "Наградено качество",
    "awards.qualityIndicators.0.description": "Последователно признание за изключителност във фотографията",
    "awards.qualityIndicators.1.title": "Професионални стандарти",
    "awards.qualityIndicators.1.description": "Спазване на най-добрите практики и етика в индустрията",
    "awards.qualityIndicators.2.title": "Удовлетворение на клиентите",
    "awards.qualityIndicators.2.description": "Доверени от клиенти в множество индустрии",
    "awards.qualityIndicators.3.title": "Години изключителност",
    "awards.qualityIndicators.3.description": "Над 8 години посветен професионален опит",
    "awards.closingStatement": "Всеки проект се подхожда със същата посветеност към изключителността, внимание към детайлите и ангажимент за предоставяне на резултати, които надхвърлят очакванията.",
    
    // Social Card
    "social.title": "Свържете се & Изследвайте",
    "social.description": "Прегледайте пълното ми портфолио във Flickr или ми изпратете съобщение директно във Facebook",
    "social.contactAvailable": "Контакт достъпен",
    "social.portfolioReady": "Портфолио готово",
    "social.facebook.name": "Facebook",
    "social.facebook.description": "Изпратете ми съобщение за запитвания и ме следвайте за актуализации",
    "social.facebook.stats": "Контакт & Следване",
    "social.flickr.name": "Flickr",
    "social.flickr.description": "Прегледайте пълното ми фотографско портфолио",
    "social.flickr.stats": "Пълно портфолио",
    "social.clickToVisit": "Кликнете, за да посетите →",
    "social.availableForContact": "Достъпен за контакт",
    "social.connectOnSocial": "Свържете се в социалните мрежи",
    "social.additionalInfo": "Свържете се с мен във Facebook за запитвания • Прегледайте пълното портфолио във Flickr • Редовни актуализации",
    
    // Contact
    "contact.title": "Свържете се с мен",
    "contact.description": "Имате проект наум? Нека обсъдим как можем да осъществим вашата визия.",
    "contact.name": "Име",
    "contact.namePlaceholder": "Вашето име",
    "contact.email": "Имейл",
    "contact.emailPlaceholder": "вашият.имейл@пример.com",
    "contact.brief": "Описание на проекта",
    "contact.briefPlaceholder": "Разкажете ми за вашия проект, сроковете и всякакви специфични изисквания...",
    "contact.minCharacters": "Минимум 20 символа",
    "contact.submit": "Изпрати съобщение",
    "contact.submitting": "Изпращане...",
    "contact.successTitle": "Съобщението е изпратено!",
    "contact.successMessage": "Благодаря ви за съобщението. Ще ви отговоря в рамките на 24 часа.",
    "contact.privacyNotice": "Вашата информация е защитена и ще бъде използвана само за отговор на вашата заявка.",
    "contact.directContact": "Предпочитате директен контакт?",
    "contact.emailLabel": "Изпратете ми имейл директно:",
    "contact.responseTime": "Време за отговор",
    "contact.hoursResponse": "Обикновено отговарям в рамките на 24 часа през работните дни.",
    "contact.secureSubmission": "Сигурно изпращане",
    "contact.secureForm": "Сигурна форма",
    "contact.quickResponse": "Бърз отговор",
    
    // Footer
    "footer.photographer": "Професионален фотограф",
    "footer.location": "Букурещ, Румъния",
    "footer.rights": "Dumitrescu Photography. Всички права запазени.",
    "footer.allRightsReserved": "Всички права запазени.",
    "footer.connectWithMe": "Свържете се с мен",
    "footer.socialDescription": "Изпратете ми съобщение във Facebook за запитвания или прегледайте пълното ми портфолио във Flickr",
    "footer.facebook.name": "Facebook",
    "footer.facebook.visit": "Посетете",
    "footer.flickr.name": "Flickr",
    "footer.flickr.visit": "Посетете",
    "footer.emailAddress": "contact@dumitrescuphotography.com",
  }
};

// Process steps data
const processStepsData = {
  EN: [
    {
      number: "01",
      title: "Consultation",
      description: "We begin with a detailed discussion to understand your vision, requirements, and objectives for the project.",
      icon: "Target",
      technicalDetails: ["Initial briefing", "Concept development", "Timeline planning"]
    },
    {
      number: "02",
      title: "Planning",
      description: "Meticulous preparation including location scouting, equipment selection, and technical setup.",
      icon: "Palette",
      technicalDetails: ["Location assessment", "Equipment preparation", "Lighting setup"]
    },
    {
      number: "03",
      title: "Execution",
      description: "Precision shooting with attention to detail, composition, and capturing the essence of the subject.",
      icon: "Zap",
      technicalDetails: ["Technical shooting", "Creative direction", "Quality control"]
    }
  ],
  RO: [
    {
      number: "01",
      title: "Consultanță",
      description: "Începem cu o discuție detaliată pentru a înțelege viziunea, cerințele și obiectivele tale pentru proiect.",
      icon: "Target",
      technicalDetails: ["Briefing inițial", "Dezvoltarea conceptului", "Planificarea termenelor"]
    },
    {
      number: "02",
      title: "Planificare",
      description: "Pregătire meticuloasă inclusiv căutarea locației, selectarea echipamentului și configurarea tehnică.",
      icon: "Palette",
      technicalDetails: ["Evaluarea locației", "Pregătirea echipamentului", "Configurarea iluminării"]
    },
    {
      number: "03",
      title: "Execuție",
      description: "Fotografiere de precizie cu atenție la detalii, compoziție și captarea esenței subiectului.",
      icon: "Zap",
      technicalDetails: ["Fotografiere tehnică", "Direcție creativă", "Controlul calității"]
    }
  ],
  BG: [
    {
      number: "01",
      title: "Консултация",
      description: "Започваме с подробна дискусия, за да разбера вашата визия, изискванията и целите за проекта.",
      icon: "Target",
      technicalDetails: ["Първоначален брифинг", "Разработване на концепцията", "Планиране на сроковете"]
    },
    {
      number: "02",
      title: "Планиране",
      description: "Изчерпателна подготовка, включително търсене на локация, избор на оборудване и техническа настройка.",
      icon: "Palette",
      technicalDetails: ["Оценка на местоположението", "Подготовка на оборудването", "Настройка на осветлението"]
    },
    {
      number: "03",
      title: "Изпълнение",
      description: "Прецизна заснемане с внимание към детайлите, композицията и заснемане на същността на обекта.",
      icon: "Zap",
      technicalDetails: ["Техническа снимка", "Творческа насока", "Контрол на качеството"]
    }
  ]
};

// Quality indicators data - using proper icon names
const qualityIndicatorsData = {
  EN: [
    {
      iconName: "Award",
      title: "Award-Winning Quality",
      description: "Consistent recognition for excellence in photography",
      color: "text-primary",
      delay: 0.1,
    },
    {
      iconName: "Shield",
      title: "Professional Standards",
      description: "Adherence to industry best practices and ethics",
      color: "text-primary",
      delay: 0.2,
    },
    {
      iconName: "Users",
      title: "Client Satisfaction",
      description: "Trusted by clients across multiple industries",
      color: "text-primary",
      delay: 0.3,
    },
    {
      iconName: "Clock",
      title: "Years of Excellence",
      description: "8+ years of dedicated professional experience",
      color: "text-primary",
      delay: 0.4,
    },
  ],
  RO: [
    {
      iconName: "Award",
      title: "Calitate Premiată",
      description: "Recunoaștere consistentă pentru excelență în fotografie",
      color: "text-primary",
      delay: 0.1,
    },
    {
      iconName: "Shield",
      title: "Standarde Profesionale",
      description: "Respectarea celor mai bune practici și etică din industrie",
      color: "text-primary",
      delay: 0.2,
    },
    {
      iconName: "Users",
      title: "Satisfacția Clienților",
      description: "De încredere de către clienți din multiple industrii",
      color: "text-primary",
      delay: 0.3,
    },
    {
      iconName: "Clock",
      title: "Ani de Excelență",
      description: "Peste 8 ani de experiență profesională dedicată",
      color: "text-primary",
      delay: 0.4,
    },
  ],
  BG: [
    {
      iconName: "Award",
      title: "Наградено качество",
      description: "Последователно признание за изключителност във фотографията",
      color: "text-primary",
      delay: 0.1,
    },
    {
      iconName: "Shield",
      title: "Професионални стандарти",
      description: "Спазване на най-добрите практики и етика в индустрията",
      color: "text-primary",
      delay: 0.2,
    },
    {
      iconName: "Users",
      title: "Удовлетворение на клиентите",
      description: "Доверени от клиенти в множество индустрии",
      color: "text-primary",
      delay: 0.3,
    },
    {
      iconName: "Clock",
      title: "Години изключителност",
      description: "Над 8 години посветен професионален опит",
      color: "text-primary",
      delay: 0.4,
    },
  ]
};

// Social links data for SocialCard - using proper icon names
const socialCardLinksData = {
  EN: [
    {
      name: "Facebook",
      description: "Message me for inquiries & follow for updates",
      url: "https://www.facebook.com/adrian.dumitrescu.9231",
      iconName: "Facebook",
      color: "#1877F2",
      stats: "Contact & Follow",
    },
    {
      name: "Flickr",
      description: "Review my full photography portfolio",
      url: "https://www.flickr.com/people/dumitrescuphotography/",
      iconName: "Camera",
      color: "#0063DC",
      stats: "Full Portfolio",
    },
  ],
  RO: [
    {
      name: "Facebook",
      description: "Trimite-mi un mesaj pentru întrebări și urmărește-mă pentru actualizări",
      url: "https://www.facebook.com/adrian.dumitrescu.9231",
      iconName: "Facebook",
      color: "#1877F2",
      stats: "Contact & Urmărire",
    },
    {
      name: "Flickr",
      description: "Vezi portofoliul meu complet de fotografie",
      url: "https://www.flickr.com/people/dumitrescuphotography/",
      iconName: "Camera",
      color: "#0063DC",
      stats: "Portofoliu Complet",
    },
  ],
  BG: [
    {
      name: "Facebook",
      description: "Изпратете ми съобщение за запитвания и ме следвайте за актуализации",
      url: "https://www.facebook.com/adrian.dumitrescu.9231",
      iconName: "Facebook",
      color: "#1877F2",
      stats: "Контакт & Следване",
    },
    {
      name: "Flickr",
      description: "Прегледайте пълното ми фотографско портфолио",
      url: "https://www.flickr.com/people/dumitrescuphotography/",
      iconName: "Camera",
      color: "#0063DC",
      stats: "Пълно портфолио",
    },
  ]
};

// Social links data for Footer - using proper icon names
const footerSocialLinksData = {
  EN: [
    {
      name: "Facebook",
      url: "https://www.facebook.com/adrian.dumitrescu.9231",
      iconName: "Facebook",
      color: "#1877F2",
    },
    {
      name: "Flickr",
      url: "https://www.flickr.com/people/dumitrescuphotography/",
      iconName: "Camera",
      color: "#0063DC",
    },
  ],
  RO: [
    {
      name: "Facebook",
      url: "https://www.facebook.com/adrian.dumitrescu.9231",
      iconName: "Facebook",
      color: "#1877F2",
    },
    {
      name: "Flickr",
      url: "https://www.flickr.com/people/dumitrescuphotography/",
      iconName: "Camera",
      color: "#0063DC",
    },
  ],
  BG: [
    {
      name: "Facebook",
      url: "https://www.facebook.com/adrian.dumitrescu.9231",
      iconName: "Facebook",
      color: "#1877F2",
    },
    {
      name: "Flickr",
      url: "https://www.flickr.com/people/dumitrescuphotography/",
      iconName: "Camera",
      color: "#0063DC",
    },
  ]
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguage] = useState<Language>("EN");

  const toggleLanguage = () => {
    setLanguage(prev => {
      switch (prev) {
        case "EN": return "RO";
        case "RO": return "BG";
        case "BG": return "EN";
        default: return "EN";
      }
    });
  };

  const t = (key: string): string => {
    const translation = translations[language][key as keyof typeof translations[typeof language]];
    return translation || key;
  };

  const getProcessSteps = () => {
    return processStepsData[language];
  };

  const getQualityIndicators = () => {
    return qualityIndicatorsData[language];
  };

  const getSocialLinks = () => {
    return socialCardLinksData[language];
  };

  const getFooterSocialLinks = () => {
    return footerSocialLinksData[language];
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        toggleLanguage,
        t,
        getProcessSteps,
        getQualityIndicators,
        getSocialLinks,
        getFooterSocialLinks,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
